// Announcement Service
import { supabase } from '../utils/supabaseClient';
import {
  Announcement,
  UpsertAnnouncementPayload,
  GetAnnouncementsPayload,
  AnnouncementResponse,
} from '../types/announcement';

/**
 * Upsert (Create/Update/Delete) an announcement
 * @param payload - The announcement payload
 * @returns Response with success status and message
 */
export const upsertAnnouncement = async (
  payload: UpsertAnnouncementPayload
): Promise<AnnouncementResponse> => {
  try {
    const { data, error } = await supabase.rpc('upsert_announcement', {
      payload: payload as any,
    });

    if (error) {
      console.error('Error upserting announcement:', error);
      return {
        success: false,
        message: error.message || 'Failed to save announcement',
      };
    }

    return data as unknown as AnnouncementResponse;
  } catch (error: any) {
    console.error('Unexpected error upserting announcement:', error);
    return {
      success: false,
      message: error.message || 'An unexpected error occurred',
    };
  }
};

/**
 * Get announcements with optional filtering and pagination
 * @param payload - Query parameters
 * @returns Response with announcements data
 */
export const getAnnouncements = async (
  payload: GetAnnouncementsPayload
): Promise<AnnouncementResponse> => {
  try {
    const { data, error } = await supabase.rpc('get_announcements', {
      payload: payload as any,
    });

    if (error) {
      console.error('Error fetching announcements:', error);
      return {
        success: false,
        message: error.message || 'Failed to fetch announcements',
        data: [],
        total_count: 0,
      };
    }

    return data as unknown as AnnouncementResponse;
  } catch (error: any) {
    console.error('Unexpected error fetching announcements:', error);
    return {
      success: false,
      message: error.message || 'An unexpected error occurred',
      data: [],
      total_count: 0,
    };
  }
};

/**
 * Create a new announcement
 */
export const createAnnouncement = async (
  announcement: Omit<Announcement, 'id'>
): Promise<AnnouncementResponse> => {
  const payload: UpsertAnnouncementPayload = {
    branch_id: announcement.branch_id,
    type: announcement.type,
    media_type: announcement.media_type,
    media_url: announcement.media_url,
    content: announcement.content,
    ar: announcement.ar,
    start_at: announcement.start_at,
    end_at: announcement.end_at,
    priority: announcement.priority || 0,
    is_active: announcement.is_active,
  };

  return upsertAnnouncement(payload);
};

/**
 * Update an existing announcement
 */
export const updateAnnouncement = async (
  id: string,
  updates: Partial<Announcement>
): Promise<AnnouncementResponse> => {
  const payload: UpsertAnnouncementPayload = {
    id,
    ...updates,
  };

  return upsertAnnouncement(payload);
};

/**
 * Delete an announcement
 */
export const deleteAnnouncement = async (
  id: string
): Promise<AnnouncementResponse> => {
  const payload: UpsertAnnouncementPayload = {
    id,
    action_delete: true,
  };

  return upsertAnnouncement(payload);
};

/**
 * Toggle announcement active status
 */
export const toggleAnnouncementStatus = async (
  id: string,
  is_active: boolean
): Promise<AnnouncementResponse> => {
  return updateAnnouncement(id, { is_active });
};
